/*global FormController, WebView, FileReader*/

var freshdeskContext = require("../../util/freshdeskContext") ;
var freshdeskDraftHtml = require("../../util/freshdeskDraftHtml") ;

/**
 * Compose a Freshdesk ticket from inside the application.
 *
 * The requester is never picked in the form. When the session carries an Office 365
 * identity (the `adm` step-up), the ticket is filed under it. Otherwise the consultant
 * is asked for their OCI address -- see the identity block of the view.
 *
 * The form is bound to iz_freshdesk_ticket for its field definitions only : nothing is
 * written to that table from here. The local trace row is inserted by the server when
 * the ticket is actually sent to Freshdesk.
 */
var freshdeskTicketPopupController = new FormController("iz_freshdesk_ticket", {
    directory: "views/freshdesk",
    name: "freshdeskTicketPopup",
    route: "create-fd-ticket",
    buttonsHTML: "<!-- no button -->",
    openInPopup: true,
    popup: {
        //docked side panel, not a centred modal : a ticket almost always describes the screen
        //behind it, so the consultant must keep clicking in the app while describing it.
        //backdrop:false plus the pointer-events rule in the view's own <style> is what keeps
        //the application usable underneath -- see .modal-side there.
        size: "medium",
        backdrop: false,
        closeWithEsc: true,
        noHeader: true
    }
}) ;

//Leaving a page never loses the ticket : the draft is saved and the panel follows the
//consultant as the card (onClose). Without this, the composer's "create" mode made the
//leave-input check (leaveInputInterceptors.js) ask "abandon the input?" on every page change
//once the panel had followed onto a record.
freshdeskTicketPopupController.noLeaveAlert = true ;

/**
 * The composer is reachable only through the single shared `adm` login (izFreshdesk.js gates
 * every endpoint on isAdm, and the step-up check is a literal `login === "adm"`), so
 * `user_uid` alone is the same value for every consultant using this feature -- it does not
 * distinguish one from another. The O365 step-up identity's UPN is the only thing that does,
 * so it is folded into the iz_map key : each consultant gets their own draft slot. Without an
 * identity (local dev, or a database with adm_o365_disabled) everyone shares one slot, same as
 * this feature behaved before this fix.
 *
 * The read, the write and the clear must all derive this key the same way -- hence one helper
 * rather than three copies of this expression.
 */

/**
 * The docket shows names, not identifiers : a consultant reading back their own ticket should
 * see "O BUSINESS", not "2". Recomputed on every render rather than stored, so a group the
 * assistant revises mid-conversation never leaves a stale label behind.
 */
freshdeskTicketPopupController._decorateDraft = function(data){
    var groups = data.groups || [] ;
    var match = groups.filter(function(g){ return String(g.value) === String(data.groupId) ; })[0] ;
    data.groupLabel = match ? match.label : (data.groupId || "") ;

    var levels = {
        1: "priorityLow", 2: "priorityMedium", 3: "priorityHigh", 4: "priorityUrgent"
    } ;
    var key = levels[Number(data.priority)] ;
    data.priorityLabel = key ? WebView.tr("freshdesk." + key) : String(data.priority || "") ;
} ;

freshdeskTicketPopupController.draftKeyFor = function(api){
    var identity = (api.currentUser && api.currentUser.o365Identity) || null ;
    return (identity && identity.upn) ? "draft:" + identity.upn : "draft" ;
} ;

freshdeskTicketPopupController._draftKey = function(){
    return this.draftKeyFor(this.api) ;
} ;

/**
 * The card survives a reload or a change of page : the popup route is put back in the hash
 * (by the browser on a reload, by onClose on a page change), and this flag tells the panel
 * to come back as the card rather than the rail.
 * sessionStorage, not the draft row : being minimized belongs to this tab, not to the ticket.
 * The top bar entry clears it (mainController.onCreateFdTicket), so opening the panel on
 * purpose always shows the full rail.
 */
freshdeskTicketPopupController.minimizedKeyFor = function(api){
    return "owork.fdMinimized:" + api.currentUser.id + ":" + this.draftKeyFor(api) ;
} ;

freshdeskTicketPopupController.rememberMinimized = function(api, minimized){
    try{
        if(minimized){ window.sessionStorage.setItem(this.minimizedKeyFor(api), "1") ; }
        else{ window.sessionStorage.removeItem(this.minimizedKeyFor(api)) ; }
    }catch(e){ /* storage refused (private mode, quota) : the card simply does not survive */ }
} ;

freshdeskTicketPopupController._rememberMinimized = function(minimized){
    this.rememberMinimized(this.api, minimized) ;
} ;

freshdeskTicketPopupController._wasMinimized = function(){
    try{
        return window.sessionStorage.getItem(this.minimizedKeyFor(this.api)) === "1" ;
    }catch(e){ return false ; }
} ;

/**
 * Tell the top bar whether a ticket is still in progress, so its Diag'Obert entry can say so
 * while the panel is closed (mainController listens). The draft row is the truth ; this only
 * spares the top bar a read on every close.
 */
freshdeskTicketPopupController._announcePending = function(pending){
    if(this.appController){ this.appController.emit("fdDraftPending", !!pending) ; }
} ;

/**
 * The attached files outlive the panel the way the conversation does. They cannot go into the
 * iz_map draft -- a File is bytes, not JSON -- so they are held here, on this singleton, which
 * lives as long as the page. The popup's DOM, and the <input> holding them, is rebuilt on every
 * open : left there, they vanished on a mere close while the conversation citing them came back.
 * A reload still loses them ; the thread then strikes them through (fdMarkLostAttachments).
 *
 * The view keeps data.keptFiles / data.keptInline in step with its input (fdWriteFiles) ; this
 * copies them off on close, and the send and abandon paths empty them, like they drop the draft.
 */
freshdeskTicketPopupController._keepFiles = function(files, inline){
    this._kept = { key: this._draftKey(), files: files || [], inline: inline || [] } ;
} ;

freshdeskTicketPopupController._keptFor = function(key){
    var kept = this._kept ;
    return (kept && kept.key === key) ? kept : { files: [], inline: [] } ;
} ;

freshdeskTicketPopupController.prepareDataOnEnter = function(data, callback){
    var identity = (this.api.currentUser && this.api.currentUser.o365Identity) || null ;
    //mainController hands over the URL captured before the popup route was appended ;
    //the fallback covers a direct navigation straight to the composer route
    var clientUrl = (data && data.clientUrl) || freshdeskContext.screenUrlFromHref(window.location.href) ;

    this.mode = "create" ;
    //this controller is a module-level singleton reused across every open of the popup route ;
    //without resetting this here, a flag set by a successful send would silently disable
    //saveDraft() for every ticket composed afterward in the same session
    this._sent = false ;

    //both the draft and the conversation that produced it survive a close, and live until the
    //ticket is sent or the consultant abandons it -- see saveDraft()/onAbandon()
    this.api.db.multiread({
        iz_map: {
            searchFirst: {
                user_uid: this.api.currentUser.id,
                code: "freshdesk_composer",
                key: this._draftKey(),
                realm_code: ''
            }
        }
    }, function(errMap, reads){
        var saved = (!errMap && reads && reads.iz_map && reads.iz_map.value) || null ;
        //read here : the options callback below runs once the lists come back
        var kept = this._keptFor(this._draftKey()) ;
        var minimized = this._wasMinimized() ;
        //the panel is back : the next close is a real one again (see onClose)
        this._followingPage = false ;

        //groups and ticket types belong to the Freshdesk configuration, not to this code, and
        //they are a per-ticket choice : read them live rather than hardcode a list that would
        //break the day the helpdesk is reconfigured
        this.api.izFreshdesk.options(function(err, options){
            var opts = options || {} ;
            callback(null, {
                o365Identity: identity,
                senderName: identity ? (identity.displayName || identity.upn) : null,
                requester_upn: "",
                attachments: [],
                clientUrl: clientUrl,
                environment: freshdeskContext.environmentFromHost(window.location.hostname),
                groups: opts.groups || [],
                types: opts.types || [],
                //the composer still opens when the lists cannot be read, so the reason is visible
                optionsError: err ? String(err) : null,
                //conversation state, restored whole. `saved.phase` is absent from rows written
                //before conversations were persisted : those carry a finished draft and nothing
                //else, so a subject is what tells us they belong in review.
                phase: saved ? (saved.phase || (saved.subject ? "review" : "chat")) : "chat",
                //the card is the conversation bar : in review it would otherwise be folded away
                chatOpen: minimized,
                //the drafted ticket reads back in a 420px rail ; previewFullscreen promotes
                //that same block to a centred overlay when it is too long to take in there
                previewFullscreen: false,
                //the panel shrunk to a card in the bottom right corner, so the consultant can
                //work in the app with the conversation still at hand. Opening from the top bar
                //starts as the full rail ; a reload puts the card back -- see _wasMinimized()
                minimized: minimized,
                messages: (saved && Array.isArray(saved.messages)) ? saved.messages : [],
                //the files attached before the panel was last closed : the view puts them back on
                //its fresh input -- see _keepFiles()
                keptFiles: kept.files,
                keptInline: kept.inline,
                userMessage: (saved && saved.userMessage) || "",
                assistantError: null,
                subject: saved ? saved.subject : "",
                //sanitized again on the way back in : a draft saved before the fix may still carry markup
                description: saved ? freshdeskDraftHtml.sanitizeDraftHtml(saved.description) : "",
                groupId: saved ? (saved.groupId || "") : "",
                ticketType: saved ? (saved.ticketType || "") : "",
                priority: saved ? (saved.priority || 2) : 2,
                //true for the whole round trip of one converse() call ; guards against a second
                //turn being pushed (mouse, Enter key, or otherwise) while the first is still in flight
                requesterEmail: (saved && saved.requesterEmail) || "",
                //a turn asked before a page change is still on its way : the reopened panel
                //waits for it like the old one did, or a second message would follow the first
                assistantBusy: !!this._askInFlight
            }) ;
        }.bind(this)) ;
    }.bind(this)) ;
} ;

/**
 * Read the picked files as base64 : the endpoint layer has no multipart parser, so the
 * bytes travel inside the JSON body and the server turns them back into a Freshdesk upload.
 */
//Freshdesk's cap per ticket, checked by the server too (izFreshdesk.js MAX_ATTACHMENTS_BYTES).
//Checked here first : base64 grows the files by a third, and past the 50 MB body limit the
//server answers a bare 413 instead of saying what is wrong
var FD_MAX_ATTACHMENTS_BYTES = 20 * 1024 * 1024 ;

function readAttachments(files){
    return Promise.all((files || []).map(function(file){
        return new Promise(function(resolve, reject){
            var reader = new FileReader() ;
            reader.onerror = function(){ reject(reader.error) ; } ;
            reader.onload = function(){
                //dataURL is "data:<mime>;base64,<payload>" -- keep only the payload
                var result = String(reader.result) ;
                resolve({
                    name: file.name,
                    type: file.type || "application/octet-stream",
                    dataBase64: result.substring(result.indexOf(",") + 1)
                }) ;
            } ;
            reader.readAsDataURL(file) ;
        }) ;
    })) ;
}

/**
 * One conversation turn. The whole history goes up each time -- the server is stateless.
 * When the assistant says it is ready, the popup switches to review with its draft.
 */
/**
 * The files attached right now, named. The FileList on the input is the single source of truth:
 * onSend reads that very list to build the Freshdesk ticket, so nothing derived from it here can
 * drift from what a customer will actually receive.
 */
freshdeskTicketPopupController._liveAttachments = function(){
    var input = this.view.EL.fdAttachments ;
    var files = (input && input.files) ? Array.prototype.slice.call(input.files) : [] ;
    //size travels too : the view matches a stamped file against the input on name AND size, and
    //name alone would hide a different file that happens to share one
    return files.map(function(f){ return { name: f.name, type: f.type || "", size: f.size } ; }) ;
} ;

freshdeskTicketPopupController.onAsk = function(){
    //one turn in flight at a time : without this, a turn typed and sent while the previous
    //one is still waiting on the server would push a second "user" entry before the first
    //got its "assistant" reply, leaving data.messages with two consecutive same-role turns --
    //exactly what buildConverseMessages does not repair. Return before touching anything,
    //including this.view.updateData(), so a stray Enter keystroke while busy is a pure no-op
    if(this.data.assistantBusy){ return ; }

    this.view.updateData() ;
    var data = this.data ;
    var text = (data.userMessage || "").trim() ;
    if(!text){ return ; }

    data.assistantBusy = true ;
    //numbered so the answer can tell whether it still belongs : abandon forgets the turn, and a
    //page change reopens the panel on a new data object (see onClose) while it is on its way
    this._askSeq = (this._askSeq || 0) + 1 ;
    var ask = this._askSeq ;
    this._askInFlight = ask ;
    var attached = this._liveAttachments() ;
    var turn = { role: "user", content: text } ;
    if(attached.length > 0){
        //stamped for the THREAD only : which files rode along with this message. What the model
        //is told is the live list, folded into the context block below -- never this record,
        //which is history and may name a file since dropped.
        turn.files = attached ;
        turn.filesLabel = attached.map(function(a){ return a.name ; }).join(" · ") ;
    }
    data.messages.push(turn) ;
    data.userMessage = "" ;
    data.assistantError = null ;
    this.view.render() ;

    //No longTask here : its full-screen overlay hides the very conversation the consultant is
    //waiting on. assistantBusy already disables the bar, and the thread shows a typing bubble,
    //which is where the eye already is. longTask stays on send, where blocking is the point.
    var draft = data.phase === "review" ? {
        subject: data.subject, description: data.description,
        groupId: data.groupId, ticketType: data.ticketType, priority: data.priority
    } : null ;

    this.api.izFreshdesk.converse(data.messages,
        { clientUrl: data.clientUrl, environment: data.environment, attachments: attached },
        draft,
        {
            groups: data.groups, types: data.types
            //needsRequesterEmail and allowedDomains are deliberately NOT sent : the server
            //derives both from the session, so posting them here could only weaken them
        },
        function(err, result){
            //abandoned meanwhile : the conversation this answers no longer exists
            if(this._askInFlight !== ask){ return ; }
            this._askInFlight = null ;
            //the panel followed a page change and was rebuilt from the saved draft : the answer
            //goes to the conversation on screen, which must still end on the question it answers
            if(this.data && this.data !== data){
                var current = this.data ;
                var asked = current.messages && current.messages[current.messages.length - 1] ;
                if(!asked || asked.role !== "user" || asked.content !== text){
                    current.assistantBusy = false ;
                    this.view.render() ;
                    return ;
                }
                data = current ;
            }
            if(err){
                //the turn never reached the model : pop the optimistic push so the history
                //never carries two consecutive "user" turns (buildConverseMessages does not
                //repair that), and hand the text back so the consultant does not retype it
                data.messages.pop() ;
                data.userMessage = text ;
                //translate the bare code the way the error dialog does (Bootstrap4Dialogs.js
                //:170) rather than showing the consultant a raw FD_* token, and fall back to
                //the code itself when no entry exists
                var code = String(err) ;
                var translated = WebView.tr("error." + code) ;
                data.assistantError = translated === "error." + code ? code : translated ;
                //clear the guard on this path too, or a failed turn would permanently lock
                //the composer -- the consultant must be able to retry
                data.assistantBusy = false ;
                this.view.render() ;
                return ;
            }
            data.messages.push({ role: "assistant", content: result.reply }) ;
            //`result.related` is deliberately NOT displayed. The owner's flow is that the search
            //surfaces through Diag'Obert's own answer ("J'ai trouvé une info ... [1]") or not at
            //all: a panel of similar tickets announces that a search happened, which breaks the
            //rule that a fruitless search must leave no trace. The server still returns it, so
            //showing it again is a view change, not a plumbing one.
            if(result.ready && result.draft){
                data.phase = "review" ;
                data.subject = result.draft.subject ;
                //the model may have copied pasted customer markup : it is put back into the panel as HTML
                data.description = freshdeskDraftHtml.sanitizeDraftHtml(result.draft.description) ;
                data.groupId = result.draft.groupId || "" ;
                data.ticketType = result.draft.ticketType || "" ;
                data.priority = result.draft.priority || 2 ;
                data.requesterEmail = result.draft.requesterEmail || data.requesterEmail || "" ;
                this._decorateDraft(data) ;
                //the card shows the conversation, not the draft : a consultant new to Diag'Obert
                //read "here is the ticket" there as already sent. The rail opens on the draft
                if(data.minimized){
                    data.minimized = false ;
                    this._rememberMinimized(false) ;
                }
            }
            data.assistantBusy = false ;
            this.view.render() ;
            //the exchange is stored as it grows, not only on close : a reload of the page
            //must not cost the consultant the turns since the panel opened
            this.saveDraft() ;
        }.bind(this)) ;
} ;

/**
 * Enlarge the drafted ticket. The same fieldset is promoted to a centred overlay by a class
 * the view derives from this flag -- there is no second copy of the docket to keep in step.
 */
freshdeskTicketPopupController.onExpandPreview = function(){
    this.data.previewFullscreen = true ;
    this.view.render() ;
} ;

freshdeskTicketPopupController.onCollapsePreview = function(){
    this.data.previewFullscreen = false ;
    this.view.render() ;
} ;

//clicking the backdrop is the same gesture as the compress button, and Esc reaches
//collapsePreview directly (see fdEscape in the view)
freshdeskTicketPopupController.onDismissPreview = function(){
    this.onCollapsePreview() ;
} ;

//the enlarged draft's own way out, at the foot of it where the eye ends up after reading
freshdeskTicketPopupController.onCollapsePreviewBack = function(){
    this.onCollapsePreview() ;
} ;

freshdeskTicketPopupController.onSendFromPreview = function(){
    this.onSend() ;
} ;

freshdeskTicketPopupController.onToggleChat = function(){
    this.data.chatOpen = !this.data.chatOpen ;
    this.view.render() ;
} ;

freshdeskTicketPopupController.onSend = function(){
    this.view.updateData() ;
    var data = this.data ;

    //read the input straight from the DOM : relying on a change listener to stash the files
    //on the bound object added a link that could silently drop them
    var input = this.view.EL.fdAttachments ;
    var files = input && input.files ? Array.prototype.slice.call(input.files) : [] ;

    this.view.longTask(function(done){
        var total = files.reduce(function(sum, f){ return sum + (f.size || 0) ; }, 0) ;
        if(total > FD_MAX_ATTACHMENTS_BYTES){ return done("FD_ATTACHMENTS_TOO_BIG") ; }
        readAttachments(files).then(function(attachments){
            this.api.izFreshdesk.send(
                data.subject, data.description,
                //no field left to type an address into : it is either proven by Office 365 or
                //it was asked for, and answered, in the conversation itself
                (this.api.currentUser && this.api.currentUser.o365Identity &&
                    this.api.currentUser.o365Identity.upn) || data.requesterEmail,
                data.groupId, data.ticketType, data.priority,
                data.clientUrl, data.environment, null, attachments,
                function(err, result){
                    if(err){ return done(err) ; }
                    this.mode = "read" ; //avoid the abort input warning on close
                    //the popup's "close" event (fired by view.close() below) also runs onClose,
                    //which would otherwise call saveDraft() and resurrect the very row this
                    //clears -- this flag tells saveDraft the ticket is already sent
                    this._sent = true ;
                    this._keepFiles([], []) ;   //they went out with the ticket
                    //the draft did its job ; leaving it behind would resurface a sent ticket as
                    //a stale draft next time the composer opens. Fired without waiting on its
                    //callback : the ticket is already sent, so a failed cleanup must not delay
                    //closing the popup
                    this.api.db.transactionalChanges([{
                        table: "iz_map",
                        action: "removeWhere",
                        record: { code: "freshdesk_composer", user_uid: this.api.currentUser.id, key: this._draftKey(), realm_code: '' }
                    }], function(){}) ;
                    //the ticket number is a link, so the message is HTML : Bootstrap4Dialogs
                    //replaces info() on BOTH the prototype and the global (extendsGlobal, read
                    //by registerExtension in WebView.js), and its modal injects the message into
                    //.modal-body as-is. escapeValue:false because i18next escapes interpolated
                    //values by default, `/` included. Nothing a consultant types reaches this
                    //message -- but the root does come from the Freshdesk settings row, so the
                    //url is encoded rather than trusted : encodeURI turns a stray quote or
                    //angle bracket into %22/%3C and it cannot break out of the href.
                    WebView.info(WebView.tr("freshdesk.sent", {
                        id: result.ticketId,
                        url: encodeURI(result.url || ""),
                        interpolation: { escapeValue: false }
                    })) ;
                    this.view.close() ;
                    done() ;
                }.bind(this)) ;
        }.bind(this)).catch(done) ;
    }.bind(this)) ;
} ;

/**
 * Minimize and restore only flip a flag : the card IS the panel, restyled (see .fd-side-min in
 * the view). Nothing is rebuilt, so the conversation, the typed message and the attachments
 * cannot be lost on the way.
 */
freshdeskTicketPopupController.onMinimizePanel = function(){
    this.view.updateData() ;
    this.data.minimized = true ;
    //the card is a conversation bar : in review the chat is otherwise folded away
    this.data.chatOpen = true ;
    this.data.previewFullscreen = false ;
    this.view.render() ;
    //the consultant goes back to work in the app, where a reload can come any time
    this.saveDraft() ;
    this._rememberMinimized(true) ;
} ;

freshdeskTicketPopupController.onRestorePanel = function(){
    this.view.updateData() ;
    this.data.minimized = false ;
    this.view.render() ;
    this._rememberMinimized(false) ;
} ;

freshdeskTicketPopupController.onCancel = function(){
    this.mode = "read" ; //avoid the abort input warning
    this.view.close() ;
} ;

//the header cross : the framework's own auto-emit names the event from data-original-id, which
//is far more reliable than wiring a click by hand against a rewritten id
freshdeskTicketPopupController.onClosePanel = function(){
    this.onCancel() ;
} ;

/**
 * Give up on the ticket in progress. Closing keeps the draft on purpose -- that was the point of
 * persisting it -- so there has to be a way to say "no, throw this away" instead of being handed
 * yesterday's abandoned ticket every time the panel opens.
 */
freshdeskTicketPopupController.onAbandon = function(){
    WebView.confirm(WebView.tr("freshdesk.abandonConfirm"), function(yes){
        if(!yes){ return ; }
        var data = this.data ;
        data.messages = [] ;
        data.userMessage = "" ;
        data.assistantError = null ;
        data.assistantBusy = false ;
        //an answer still on its way belongs to what is being abandoned : drop it on arrival
        this._askInFlight = null ;
        data.phase = "chat" ;
        data.subject = "" ;
        data.description = "" ;
        data.groupId = "" ;
        data.ticketType = "" ;
        data.priority = 2 ;
        data.groupLabel = "" ;
        data.priorityLabel = "" ;
        //the docket is gone with the draft ; leaving this set would strand the backdrop
        data.previewFullscreen = false ;

        //the files belong to the ticket being abandoned : the input is not data-bound, so the
        //render below would leave them attached to the next conversation
        data.keptFiles = [] ;
        data.keptInline = [] ;
        this._keepFiles([], []) ;
        this._announcePending(false) ;
        this._rememberMinimized(false) ;
        var input = this.view.EL.fdAttachments ;
        if(input && typeof DataTransfer !== "undefined"){ input.files = new DataTransfer().files ; }

        //drop the stored draft too, or the next open would resurrect what was just abandoned
        this.api.db.transactionalChanges([{
            table: "iz_map",
            action: "removeWhere",
            //removeWhere reads `record`, NOT `conditions` (Database.js:603-606). Passing
            //conditions leaves record undefined, so the delete silently matches nothing and the
            //abandoned draft comes back on the next open -- which is exactly what happened.
            record: {
                code: "freshdesk_composer", user_uid: this.api.currentUser.id,
                key: this._draftKey(), realm_code: ''
            }
        }], function(){ /* a failed delete must not block the composer */ }) ;

        //phase is back to "chat", so onClose's saveDraft returns early and cannot resurrect
        //what was just discarded. _sent is left alone : it belongs to the send path.
        this.view.render() ;
    }.bind(this)) ;
} ;

/**
 * The conversation and the drafted ticket both outlive the popup. They are kept until the
 * ticket is sent or the consultant abandons it -- those two paths delete the row, and they are
 * the only ones that do. The original design made the dialogue ephemeral on purpose ; field
 * use overturned it, because leaving the panel to go and read the answer to the assistant's
 * own question cost the consultant the entire exchange.
 *
 * A failed save is a lost convenience, not a blocker : the composer must stay usable either way.
 */
freshdeskTicketPopupController.saveDraft = function(){
    if(this._sent){ return ; } //the ticket already went out ; nothing left to draft
    var data = this.data ;
    if(!data){ return ; }
    //nothing said and nothing drafted : a panel merely opened and closed must not leave a row
    //behind, or the next open would hand back an empty "resumed" conversation. Same condition
    //as the Abandonner button's own visibility (freshdeskTicketPopup.html), which is the gesture
    //that undoes this one -- plus a first message still being typed : the panel follows the
    //consultant to another page by closing and reopening, and must not drop it on the way.
    if(!(data.messages && data.messages.length) && !data.subject && !(data.userMessage || "").trim()){ return ; }
    var key = this._draftKey() ;
    this.api.db.transactionalChanges([{
        table: "iz_map",
        action: "insertOrUpdate",
        conditions: { code: "freshdesk_composer", user_uid: this.api.currentUser.id, key: key, realm_code: '' },
        record: {
            code: "freshdesk_composer", user_uid: this.api.currentUser.id, key: key, realm_code: '',
            value: {
                subject: data.subject, description: data.description,
                groupId: data.groupId, ticketType: data.ticketType, priority: data.priority,
                requesterEmail: data.requesterEmail,
                //the exchange itself, plus the phase it had reached, so reopening lands the
                //consultant exactly where they left -- chat mid-question, or review on a draft
                phase: data.phase,
                messages: data.messages || [],
                //a message typed but not yet sent is part of what was interrupted
                userMessage: data.userMessage || ""
            }
        }
    }], function(){ /* a failed save must not block the composer */ }) ;
} ;

/**
 * Was the panel closed by a change of page rather than by the consultant? A page change drops
 * the popup route from the hash, and the router closes the view once currentRoute already
 * reflects that. Every close the consultant asks for (the cross, Esc, send, abandon's reset)
 * closes the view first and only then navigates ".." (ViewController.enter, the "close"
 * listener), so the route is still there when onClose runs.
 */
freshdeskTicketPopupController._closedByPageChange = function(){
    var app = this.appController ;
    if(this._sent || !app || !app.currentRoute || !this.api.currentUser){ return false ; }
    var route = this.viewOptions.route ;
    var routes = app._getRoutes(app.currentRoute) ;
    //no route left at all is a logout or a dead link : nowhere to put the card back
    return routes.length > 0 && !routes.some(function(r){ return r.route === route ; }) ;
} ;

/**
 * Opening a record from a list (products -> one product) keeps the popup route in the hash but
 * stacks it under the record, and the default stack() hides the view : what was left was the
 * empty white rail of the modal. A page change shrinks the panel to the card instead, so a
 * stacked panel does the same, and stays on screen.
 */
freshdeskTicketPopupController.stack = function(){
    this.emit("beforeStack") ;
    if(this.data && !this.data.minimized){ this.onMinimizePanel() ; }
    this.emit("stack") ;
} ;

/**
 * Closed on purpose while stacked under a record, the popup route is still in the hash : the
 * ".." the framework navigates on close only fires when the popup is the top route
 * (ViewController.enter). Left there, a reload or a back from the record would bring back a
 * panel the consultant closed.
 */
freshdeskTicketPopupController._dropStackedRoute = function(){
    var app = this.appController ;
    if(!app || !app.currentRoute){ return ; }
    var route = this.viewOptions.route ;
    var routes = app._getRoutes(app.currentRoute) ;
    var rest = routes.filter(function(r){ return r.route !== route ; }) ;
    //absent, or on top (the framework's own ".." removes it), or nothing left to stand on
    if(rest.length === routes.length || routes[routes.length - 1].route === route || rest.length === 0){ return ; }
    rest[0].route = "/" + rest[0].route ;
    setTimeout(function(){ app.navigate(rest) ; }, 0) ;
} ;

freshdeskTicketPopupController.onClose = function(){
    //Between the close that follows a page change and the panel reopening, nothing else may
    //clear the card flag : the reopen is what reads it. Everything was saved by the first pass.
    if(this._followingPage){ return ; }
    this.mode = "read" ;
    //The consultant leaves to fetch a capture on another screen : the panel follows as the
    //card. The flag is what a reload reads too, so the card reopened below and the one a
    //reload brings back are the same code path.
    var followPage = this._closedByPageChange() ;
    this._rememberMinimized(followPage) ;
    //pull whatever edits are still sitting in the DOM (subject/description/...) into
    //this.data before saving -- data-binding here is pull-based (see updateData()),
    //so skipping this would persist the last *rendered* draft, not the last *edited* one
    if(this.view && this.view.updateData){ this.view.updateData() ; }
    this.saveDraft() ;
    if(!this._sent && this.data){ this._keepFiles(this.data.keptFiles, this.data.keptInline) ; }
    //the same condition saveDraft keeps a row on
    var data = this.data ;
    this._announcePending(!this._sent && data && ((data.messages && data.messages.length) || data.subject)) ;
    if(!followPage){ this._dropStackedRoute() ; }
    if(followPage){
        //after the navigation that closed it has settled : the route goes onto the new page,
        //and the screen the ticket is about stays the one it was opened on
        var clientUrl = data && data.clientUrl ;
        this._followingPage = true ;
        var reopen = function(){
            this.appController.navigate("~" + this.viewOptions.route, { clientUrl: clientUrl }) ;
        }.bind(this) ;
        //Not before the old modal has faded out. Its hidden.bs.modal handler calls view.close()
        //on the view object this controller reuses (Bootstrap4Dialogs.js openInPopup), and
        //view.close() wipes every listener (WebView.close). Reopened sooner, the new panel had
        //its listeners -- onClose included -- wiped under it, so it survived one page change
        //and vanished on the next (measured). Registered after the dialog's own handler, this
        //one runs once that close is done.
        var $old = this.view && this.view.$modal ;
        if($old && $old[0] && document.contains($old[0])){
            $old.one("hidden.bs.modal", function(){ setTimeout(reopen, 0) ; }) ;
        }else{
            setTimeout(reopen, 0) ;
        }
    }
} ;

module.exports = freshdeskTicketPopupController ;
